// components/organisms/HeaderActions/HeaderActions.js
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import LanguageSwitcher from '../../Molecules/LanguageSwitcher';
import { FaUser, FaBars, FaSearch } from 'react-icons/fa';
import { useAuth } from '../../../contexts/AuthContext';
import Avatar from '../../Atoms/Avatar';

const SearchField = dynamic(() => import('../../Molecules/SearchField'));

const HeaderActions = ({
    onMenuToggle,
    isMenuOpen = false,
    locales = [],
    currentLocale = null,
}) => {
    const { user, isAuthenticated, logout } = useAuth();
    const [openUtility, setOpenUtility] = useState(null); // 'search' | 'user' | null

    const actionsRef = useRef(null);

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (actionsRef.current && !actionsRef.current.contains(e.target)) {
                setOpenUtility(null);
            }
        };
        const handleEscape = (e) => {
            if (e.key === 'Escape') setOpenUtility(null);
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, []);

    const toggleUtility = (name) => {
        setOpenUtility(openUtility === name ? null : name);
    };

    const isDefaultPhoto = user?.photo?.includes('default-user-avatar.png');
    const userPhoto = isDefaultPhoto ? null : user?.photo;

    const userName = user?.first_name && user?.last_name
        ? `${user.first_name} ${user.last_name}`
        : user?.display_name || user?.username;

    const authLinks = isAuthenticated
        ? [
            { title: 'Profile', url: '/auth/profile' },
            { title: 'Logout', action: 'logout' },
          ]
        : [
            { title: 'Login', url: '/auth/login' },
            { title: 'Register', url: '/auth/register' },
          ];

    const handleSearch = (query) => {
        if (query.trim()) {
            setOpenUtility(null);
            window.location.href = `/search?q=${encodeURIComponent(query)}`;
        }
    };

    const handleLinkClick = (e, link) => {
        if (link.action === 'logout') {
            e.preventDefault();
            logout();
        }
        setOpenUtility(null);
    };

    return (
        <div className="header-actions" ref={actionsRef}>

            {/* Search */}
            <div className={`header-utility ${openUtility === 'search' ? 'is-open' : ''}`}>
                <button
                    className="header-utility-button"
                    aria-label="Search"
                    aria-expanded={openUtility === 'search'}
                    onClick={() => toggleUtility('search')}
                >
                    <FaSearch className="icon-sm" />
                </button>
                <div className="header-utility-dropdown header-utility-dropdown--search">
                    <SearchField
                        onSearch={handleSearch}
                        placeholder="Search..."
                        usage="compact"
                    />
                </div>
            </div>

            {/* Language Switcher */}
            <LanguageSwitcher
                available_locales={locales}
                current_locale={currentLocale}
            />

            {/* User Menu */}
            <div className={`header-utility ${openUtility === 'user' ? 'is-open' : ''}`}>
                <button
                    className="header-utility-button"
                    aria-label="User menu"
                    aria-expanded={openUtility === 'user'}
                    onClick={() => toggleUtility('user')}
                >
                    {isAuthenticated && user ? (
                        <Avatar
                            imageUrl={userPhoto}
                            userName={userName}
                            size="sm"
                        />
                    ) : (
                        <FaUser className="icon-sm" />
                    )}
                </button>
                <div className="header-utility-dropdown header-utility-dropdown--user">
                    <ul className="header-utility-list">
                        {authLinks.map((link, index) => (
                            <li key={link.url || link.action || index}>
                                <Link
                                    href={link.url || '#'}
                                    className="header-utility-link"
                                    onClick={(e) => handleLinkClick(e, link)}
                                >
                                    {link.title}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            {/* Mobile Menu Toggle */}
            <button
                className={`header-menu-toggle ${isMenuOpen ? 'is-open' : ''}`}
                onClick={onMenuToggle}
                aria-label="Toggle navigation menu"
                aria-expanded={isMenuOpen}
            >
                <FaBars className="icon-sm" />
            </button>

        </div>
    );
};

HeaderActions.propTypes = {
    onMenuToggle: PropTypes.func.isRequired,
    isMenuOpen: PropTypes.bool,
    locales: PropTypes.array,
    currentLocale: PropTypes.object,
};

export default HeaderActions;